/* Черновик экрана на движке без React (план вехи 3, text-cover §2.2): то же правило, что у родных форм (text/native/use-text-draft.ts,
   motion/use-motion-draft.ts), и их же ключ и хранилище — черновик, начатый в одной форме, открывается в другой (симметрия отката).
   - восстановление один раз на владельца и приложение, если заявки нет и вход не задан (initialInputs): только ключи нынешней формы
     (studioPrefill), владелец проверяется после чтения — сменился или черновик битый: «заблокировано» (создание закрыто, «Сбросить черновик»);
   - запись — после правки (сразу или через delay), пока заявки нет; consent_accepted не пишется; хранилище недоступно — «заблокировано»;
   - заявка делает черновик неизменяемым: он ждёт и не перезаписывается (редактирование не меняет неизвестный POST). */
import { endpoints } from '../../../api';
import { studioPrefill } from '../studio-prefill';
import type { OrderService } from './order-service';

const KEY = /^[a-z][a-z0-9_]{0,63}$/;

export interface StudioDraft {
  /** Сверка с сервисом (каждый вид): восстановить, если пора; записать правку (через delay мс) */
  sync(): void;
  /** Черновик не сохранился или не читается: создание закрыто */
  blocked(): boolean;
  /** Черновик ещё читается (форма не готова к правкам) */
  restoring(): boolean;
  /** «Сбросить черновик»: владелец тот же — запись удаляется, блок снимается */
  reset(): Promise<void>;
  /** Кадр ушёл: таймер и чтение прерываются */
  stop(): void;
}

export interface StudioDraftOptions {
  svc: OrderService;
  /** Вход задан (initialInputs, повтор): черновик не восстанавливается */
  seeded: boolean;
  changed(): void;
  /** Хранилище родной формы этого экрана */
  storage(): Storage;
  /** Ключ родной формы: steos:<экран>:draft:<owner>:<app> */
  key(owner: number, app: number): string;
  /** Задержка записи после правки; 0 — сразу, как у родной формы без таймера */
  delay: number;
}

export function createStudioDraft(o: StudioDraftOptions): StudioDraft {
  const { svc } = o;
  /** Для какого владельца и приложения черновик готов (восстановлен или его не было) */
  let ready = '';
  let bad = '';
  let settled = '';
  let reading: AbortController | null = null;
  let timer = 0;
  /** Входы, которые уже записаны (или прочитаны): та же ссылка — записывать нечего */
  let saved: Record<string, string> | null = null;
  const identity = () => {
    const s = svc.get();
    return s.identity && s.form ? `${s.identity.owner}:${s.form.app_id}` : '';
  };
  function restore(id: string) {
    const s = svc.get();
    const form = s.form!;
    const owner = s.identity!.owner;
    ready = '';
    reading?.abort();
    const controller = new AbortController();
    reading = controller;
    void (async () => {
      try {
        const raw = o.storage().getItem(o.key(owner, form.app_id));
        if (raw && !o.seeded) {
          const value = JSON.parse(raw) as { owner: number; app: number; inputs: Record<string, string> };
          if (
            value.owner !== owner ||
            value.app !== form.app_id ||
            !value.inputs ||
            Array.isArray(value.inputs) ||
            Object.entries(value.inputs).some(([k, v]) => !KEY.test(k) || typeof v !== 'string' || v.length > 20000)
          )
            throw new Error('invalid');
          const next = await studioPrefill(form, value.inputs, null, controller.signal);
          if ((await endpoints.me()).user.id !== owner) throw new Error('owner_changed');
          if (controller.signal.aborted) return;
          svc.setters.inputs(next.inputs);
          svc.setters.uploadIds(next.uploadIds);
          saved = svc.get().inputs;
        }
        if (!controller.signal.aborted) ready = id;
      } catch {
        if (!controller.signal.aborted) bad = id;
      } finally {
        if (!controller.signal.aborted) {
          settled = id;
          if (reading === controller) reading = null;
          o.changed();
        }
      }
    })();
  }
  function save(id: string) {
    const s = svc.get();
    if (!s.identity || !s.form || s.intent || ready !== id || s.inputs === saved) return;
    window.clearTimeout(timer);
    const inputs = s.inputs;
    const owner = s.identity.owner,
      app = s.form.app_id;
    const write = () => {
      if (identity() !== id || svc.get().intent) return;
      try {
        const safe = Object.fromEntries(Object.entries(inputs).filter(([key]) => key !== 'consent_accepted'));
        o.storage().setItem(o.key(owner, app), JSON.stringify({ owner, app, inputs: safe }));
        saved = inputs;
      } catch {
        bad = id;
        o.changed();
      }
    };
    if (o.delay > 0) timer = window.setTimeout(write, o.delay);
    else write();
  }
  return {
    sync() {
      const id = identity();
      if (!id) return;
      const s = svc.get();
      if (s.intent) {
        /* заявка: черновик не трогается, форма готова */
        if (ready !== id) {
          ready = id;
          settled = id;
        }
        return;
      }
      if (ready !== id && settled !== id && !reading) {
        restore(id);
        return;
      }
      save(id);
    },
    blocked: () => !!bad && bad === identity(),
    restoring: () => {
      const id = identity();
      return !!id && !svc.get().intent && settled !== id;
    },
    async reset() {
      const s = svc.get();
      const id = identity();
      if (!s.identity || !s.form) return;
      try {
        if ((await endpoints.me()).user.id !== s.identity.owner) return;
        o.storage().removeItem(o.key(s.identity.owner, s.form.app_id));
        ready = id;
        settled = id;
        bad = '';
      } catch {
        bad = id;
      }
      o.changed();
    },
    stop() {
      window.clearTimeout(timer);
      reading?.abort();
      reading = null;
    },
  };
}
